import { useCallback, useEffect, useRef, useState } from 'react';

export const DEFAULT_FLOATING_DISMISS_GROUP = 'floating-surface';

const subscribers = new Map();
// Escape eligibility is registered for every floating surface, separately from the peer
// group: a surface that opts out of peer dismissal (`dismissGroup={null}`) still closes on
// Escape. Each entry reads its owner's live open ref; nothing here stores open state.
const escapeSurfaces = new Map();
// The one arbitration receipt per native keydown. Document listeners run in registration
// order and React can commit between trusted listeners, so a surface asking second must get
// the answer the first asker got rather than recompute it against already-closed state.
let escapeArbitration = { event: null, winnerId: null };
let nextSurfaceId = 1;
let nextOpenOrder = 1;

function getSurfaceId() {
  const id = `floating-surface-${nextSurfaceId}`;
  nextSurfaceId += 1;
  return id;
}

function getNode(refOrNode) {
  if (!refOrNode) {
    return null;
  }

  if ('current' in refOrNode) {
    return refOrNode.current;
  }

  return typeof globalThis.Node !== 'undefined' && refOrNode instanceof globalThis.Node
    ? refOrNode
    : null;
}

function isElementNode(node) {
  return typeof globalThis.Element !== 'undefined' && node instanceof globalThis.Element;
}

function shouldPreserveAncestorSurface(subscriber, sourceNodes) {
  const subscriberSurface = getNode(subscriber.surfaceRef);
  if (!isElementNode(subscriberSurface)) {
    return false;
  }

  return sourceNodes.some((sourceNode) => (
    isElementNode(sourceNode)
    && sourceNode !== subscriberSurface
    && subscriberSurface.contains(sourceNode)
  ));
}

export function registerFloatingSurface(surfaceId, group, dismiss, options = {}) {
  if (!surfaceId || !group || typeof dismiss !== 'function') {
    return () => {};
  }

  subscribers.set(surfaceId, {
    group,
    dismiss,
    surfaceRef: options.surfaceRef,
  });

  return () => {
    subscribers.delete(surfaceId);
  };
}

export function announceFloatingSurfaceOpen(sourceId, group, options = {}) {
  if (!sourceId || !group) {
    return;
  }

  const activeElement = typeof document !== 'undefined' ? document.activeElement : null;
  const sourceNodes = [
    getNode(options.contextRef),
    getNode(options.surfaceRef),
    activeElement,
  ].filter(Boolean);

  subscribers.forEach((subscriber, surfaceId) => {
    if (
      surfaceId !== sourceId
      && subscriber.group === group
      && !shouldPreserveAncestorSurface(subscriber, sourceNodes)
    ) {
      subscriber.dismiss();
    }
  });
}

function isCoveredByNestedSurface(candidate, openSurfaces) {
  const candidateSurface = getNode(candidate.surfaceRef);
  if (!isElementNode(candidateSurface)) {
    return false;
  }

  return openSurfaces.some((other) => other !== candidate && [
    getNode(other.surfaceRef),
    getNode(other.contextRef),
  ].some((node) => (
    isElementNode(node)
    && node !== candidateSurface
    && candidateSurface.contains(node)
  )));
}

function resolveFloatingEscapeWinner(event) {
  if (escapeArbitration.event === event) {
    return escapeArbitration.winnerId;
  }

  const openSurfaces = [...escapeSurfaces.values()].filter((surface) => surface.openRef.current);
  // Innermost first: a surface that contains another open surface, or the anchor a portaled
  // child opened from, is that child's ancestor. Among the rest the latest opened is topmost.
  const winner = openSurfaces
    .filter((surface) => !isCoveredByNestedSurface(surface, openSurfaces))
    .reduce((top, surface) => (!top || surface.openOrder > top.openOrder ? surface : top), null);
  escapeArbitration = { event, winnerId: winner?.surfaceId ?? null };
  return escapeArbitration.winnerId;
}

/**
 * Claims an Escape keydown for one floating surface, or reports that it must not close.
 *
 * Exactly one open floating surface acts per Escape. A nested editor's earlier
 * `preventDefault` is the claim every surface honors; otherwise the innermost/topmost open
 * surface wins and records its claim with `preventDefault` before it closes. Window-bubble
 * rungs (canvas selection, cursor) read that same flag.
 */
function claimFloatingSurfaceEscape(surfaceId, event) {
  if (!event || event.key !== 'Escape' || event.defaultPrevented) {
    return false;
  }

  if (resolveFloatingEscapeWinner(event) !== surfaceId) {
    return false;
  }

  event.preventDefault();
  return true;
}

export function clearFloatingSurfaceDismissalRegistry() {
  subscribers.clear();
  escapeSurfaces.clear();
  escapeArbitration = { event: null, winnerId: null };
  nextSurfaceId = 1;
  nextOpenOrder = 1;
}

/**
 * `onEscape` makes this hook the surface's Escape closer: one document-bubble listener while
 * open, acting only when `claimFloatingSurfaceEscape` grants it the key. Surfaces whose
 * third-party root already listens (BasePopover) omit it and gate that close with the
 * returned `claimEscape` instead, so no surface has two Escape closers.
 */
export function useFloatingSurfaceDismissal({
  open,
  dismissGroup = DEFAULT_FLOATING_DISMISS_GROUP,
  onDismiss,
  onEscape = null,
  surfaceRef = null,
  contextRef = null,
} = {}) {
  const [surfaceId] = useState(getSurfaceId);
  const openRef = useRef(open);
  const onDismissRef = useRef(onDismiss);
  const onEscapeRef = useRef(onEscape);
  const hasEscapeCloser = typeof onEscape === 'function';

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  useEffect(() => {
    onEscapeRef.current = onEscape;
  }, [onEscape]);

  useEffect(() => {
    escapeSurfaces.set(surfaceId, {
      surfaceId,
      openRef,
      surfaceRef: null,
      contextRef: null,
      openOrder: 0,
    });
    return () => {
      escapeSurfaces.delete(surfaceId);
    };
  }, [surfaceId]);

  useEffect(() => {
    const surface = escapeSurfaces.get(surfaceId);
    if (surface) {
      surface.surfaceRef = surfaceRef;
      surface.contextRef = contextRef;
    }
  }, [contextRef, surfaceId, surfaceRef]);

  useEffect(() => {
    const surface = escapeSurfaces.get(surfaceId);
    if (open && surface) {
      surface.openOrder = nextOpenOrder;
      nextOpenOrder += 1;
    }
  }, [open, surfaceId]);

  const claimEscape = useCallback((event) => (
    claimFloatingSurfaceEscape(surfaceId, event)
  ), [surfaceId]);

  useEffect(() => {
    if (!open || !hasEscapeCloser) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (claimFloatingSurfaceEscape(surfaceId, event)) {
        onEscapeRef.current?.(event);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [hasEscapeCloser, open, surfaceId]);

  useEffect(() => {
    if (!dismissGroup) {
      return undefined;
    }

    return registerFloatingSurface(
      surfaceId,
      dismissGroup,
      () => {
        if (openRef.current) {
          onDismissRef.current?.();
        }
      },
      { surfaceRef },
    );
  }, [dismissGroup, surfaceId, surfaceRef]);

  useEffect(() => {
    if (open && dismissGroup) {
      announceFloatingSurfaceOpen(surfaceId, dismissGroup, {
        surfaceRef,
        contextRef,
      });
    }
  }, [open, dismissGroup, surfaceId, surfaceRef, contextRef]);

  return { claimEscape };
}
